import React, { useMemo, useState } from 'react'
import { TextField, useTheme } from '@mui/material'
import { de } from 'date-fns/locale'
import useFavurTranslation from 'hooks/useFavurTranslation'
import { ChevronLeftMD, ChevronRightMD } from 'redesign/components/atoms/Icon'
import CalendarIcon from 'redesign/components/atoms/Icon/calendar-icon'
import ErrorMessage from 'redesign/components/atoms/error-message'
import { DATE_FORMAT_VARIANTS, DATE_FORMATS, GERMAN_DATE_FORMATS } from 'redesign/shared/date/constants'
import { formatDate } from 'redesign/shared/date/utils'
import DayPickerActionButtons from './day-picker-action-buttons'
import { DatePicker } from './styles'
import type { DayPickerProps } from './types'

const DayPicker: React.FC<DayPickerProps> = ({
  label,
  onChange,
  minDate,
  maxDate,
  dataTestId,
  required,
  error,
  helperText,
  value,
  onAccept,
}) => {
  const { t, locale } = useFavurTranslation()
  const { palette } = useTheme()
  const [focused, setFocused] = useState(false)
  const color = useMemo(() => {
    if (error) return palette.error.main

    if (focused) return palette.secondary.main

    return 'inherit'
  }, [error, focused, palette])

  return (
    <ErrorMessage
      hasError={error}
      errorMessage={
        helperText ?? t('common.form.error.required', { fieldName: t('absenceRequest.new.fromDate.label') })
      }
    >
      <DatePicker
        value={value as Date | null}
        data-testid={dataTestId}
        disablePast
        format={
          locale === de ? GERMAN_DATE_FORMATS.shortWithWeekday.otherYear : DATE_FORMATS.shortWithWeekday.otherYear
        }
        onOpen={() => setFocused(true)}
        onAccept={(newValue) => {
          setFocused(false)
          onAccept?.(newValue)
        }}
        onClose={() => setFocused(false)}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        label={label}
        slots={{
          field: (props) => (
            <TextField
              {...props}
              value={formatDate({
                date: props.value ?? '',
                locale,
                t,
                dateFormat: DATE_FORMAT_VARIANTS.shortWithWeekday,
                displayOnlyWeekdays: true,
              })}
              focused={focused && !error}
              error={error}
              required={required}
              data-testid={dataTestId}
              slotProps={{
                input: {
                  endAdornment: <CalendarIcon color={color} />,
                },
                inputLabel: { shrink: true },
              }}
            />
          ),
          leftArrowIcon: () => <ChevronLeftMD color={palette.secondary.main} />,
          rightArrowIcon: () => <ChevronRightMD color={palette.secondary.main} />,
          toolbar: undefined,
          actionBar: DayPickerActionButtons,
        }}
        views={['year', 'month', 'day']}
      />
    </ErrorMessage>
  )
}

export default DayPicker
